/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { dialogFallback } from "./fallbackauthdialog.js";
import { getGraphToken } from "./middle-tier-calls";
import { showMessage } from "./message-helper";
import { handleClientSideErrors } from "./error-handler";
import { shouldUseOfficeSSOFlow, shouldUseMSALFlow, getHostEnvironment } from "./environment-detector";

let retryGetMiddletierToken = 0;

export async function getUserProfile(callback) {
  const environment = getHostEnvironment();
  console.log(`[Auth] Detected environment: ${environment}`);

  if (shouldUseOfficeSSOFlow()) {
    console.log("[Auth] Using Office SSO flow (Desktop Word)");
    await getUserProfileWithOfficSSO(callback);
  } else if (shouldUseMSALFlow()) {
    console.log("[Auth] Using MSAL flow (Word Online or non-MS account)");
    await dialogFallback(callback);
  } else {
    console.log("[Auth] Environment unclear, trying Office SSO first");
    try {
      await getUserProfileWithOfficSSO(callback);
    } catch (error) {
      console.log("[Auth] Office SSO failed, falling back to MSAL");
      await dialogFallback(callback);
    }
  }
}

async function getUserProfileWithOfficSSO(callback) {
  try {
    let middletierToken = await Office.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      forMSGraphAccess: false,
    });
    let response = await getGraphToken(middletierToken);
    if (!response) {
      throw new Error("Middle tier didn't respond");
    } else if (response.claims) {
      let mfaMiddletierToken = await Office.auth.getAccessToken({
        authChallenge: response.claims,
      });
      response = getGraphToken(mfaMiddletierToken);
    }

    if (response.error) {
      handleAADErrors(response, callback);
    } else {
      callback(response);
    }
  } catch (exception) {
    console.log("[Office SSO] Exception:", exception);
    if (exception.code) {
      if (exception.code === 13013) {
        // User not logged in yet, retry after delay
        setTimeout(() => {
          getUserProfile(callback);
        }, 3000);
      } else if (handleClientSideErrors(exception)) {
        // Try dialog fallback (existing MSAL dialog)
        dialogFallback(callback);
      } else {
        // Office SSO failed completely, try MSAL as last resort
        console.log("[Auth] Office SSO failed, attempting MSAL fallback");
        await dialogFallback(callback);
      }
    } else {
      showMessage("EXCEPTION: " + JSON.stringify(exception));
      throw exception;
    }
  }
}

function handleAADErrors(response, callback) {
  if (response.error_description.indexOf("AADSTS500133") !== -1 && retryGetMiddletierToken <= 0) {
    retryGetMiddletierToken++;
    getUserProfile(callback);
  } else {
    dialogFallback(callback);
  }
}
